/**
 * RoleAwareRedirect -- redirects to different destinations based on user role.
 *
 * Used for /dashboard/analytics:
 * // --- FEAT-039 TASK-010 (doc) ---
 * member -> /chat, maintainer+ (admin too, hasMinimumRole) -> /admin/analytics
 * // --- fim FEAT-039 TASK-010 (doc) ---
 */

import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { hasMinimumRole } from '@/components/guards/RoleGuard';

interface RoleAwareRedirectProps {
  /** Destination for users with maintainer+ role */
  maintainerPath: string;
  /** Destination for member role (default: /chat) */
  memberPath?: string;
}

/**
 * Merge source search params into a destination path.
 * Destination params take precedence over source params with the same key.
 */
function mergeSearchParams(destPath: string, sourceSearch: string): string {
  const qIdx = destPath.indexOf('?');
  const path = qIdx >= 0 ? destPath.slice(0, qIdx) : destPath;
  const destSearch = qIdx >= 0 ? destPath.slice(qIdx + 1) : '';
  const destParams = new URLSearchParams(destSearch);
  const sourceParams = new URLSearchParams(sourceSearch);

  const merged = new URLSearchParams();
  sourceParams.forEach((value, key) => {
    merged.set(key, value);
  });
  destParams.forEach((value, key) => {
    merged.set(key, value); // destination takes precedence
  });

  const search = merged.toString();
  return search ? `${path}?${search}` : path;
}

export function RoleAwareRedirect({
  maintainerPath,
  memberPath = '/chat',
}: RoleAwareRedirectProps) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isMaintainerPlus = hasMinimumRole(user.role, 'maintainer');
  const rawDest = isMaintainerPlus ? maintainerPath : memberPath;
  const finalPath = mergeSearchParams(rawDest, location.search);

  return <Navigate to={finalPath} replace />;
}
